import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode, Html5QrcodeScanType, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { verifierMatricule, verifierFamille } from '../services/api';
import Footer from '../components/Footer';
import InstallPrompt from '../components/InstallPrompt';
import './ScanCodeBarres.css';

const ScanCodeBarres = () => {
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [searchType, setSearchType] = useState('membre'); // 'membre' ou 'famille'
  const html5QrCodeRef = useRef(null);

  const handleVerification = async (numero) => {
    console.log('=== handleVerification appelé ===');
    console.log('Numéro:', numero);
    console.log('Type de recherche:', searchType);
    
    if (loading) {
      console.log('Déjà en cours de chargement, annulé');
      return;
    }
    
    setLoading(true);
    setError('');
    setResult(null);

    try {
      let response;
      if (searchType === 'famille') {
        console.log('Appel API verifierFamille...');
        response = await verifierFamille(numero);
      } else {
        console.log('Appel API verifierMatricule...');
        response = await verifierMatricule(numero);
      }
      console.log('✅ Résultat reçu:', response.data);
      console.log('Type dans la réponse:', response.data?.type);
      setResult(response.data);
    } catch (err) {
      console.error('❌ Erreur API:', err);
      console.error('Response:', err.response);
      const typeLabel = searchType === 'famille' ? 'Famille' : 'Membre';
      setError(err.response?.data?.message || `${typeLabel} non trouvé(e) avec ce numéro`);
    } finally {
      setLoading(false);
      console.log('=== Fin handleVerification ===');
    }
  };

  const handleManualInput = async (e) => {
    e.preventDefault();
    console.log('Formulaire soumis');
    const numero = e.target.numero.value.trim();
    console.log('Numéro saisi:', numero);
    if (numero) {
      await handleVerification(numero);
    } else {
      console.log('Numéro vide, pas de vérification');
    }
  };

  const reset = () => {
    setResult(null);
    setError('');
    stopScanning();
  };

  const stopScanning = async () => {
    if (scanning && html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        await html5QrCodeRef.current.clear();
      } catch (err) {
        console.error('Erreur lors de l\'arrêt du scanner:', err);
      }
      setScanning(false);
    }
  };

  const startScanning = async () => {
    try {
      setError('');
      
      // Attendre que l'élément soit rendu dans le DOM
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const readerElement = document.getElementById("reader");
      if (!readerElement) {
        throw new Error("Élément de scanner non trouvé dans le DOM");
      }
      
      // Vérifier si le scanner est déjà initialisé
      if (html5QrCodeRef.current) {
        try {
          await html5QrCodeRef.current.stop();
          await html5QrCodeRef.current.clear();
        } catch (e) {
          // Ignorer les erreurs d'arrêt
        }
      }
      
      const html5QrCode = new Html5Qrcode("reader");
      html5QrCodeRef.current = html5QrCode;

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
        formatsToSupport: [
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.CODABAR,
          Html5QrcodeSupportedFormats.ITF
        ]
      };

      await html5QrCode.start(
        { facingMode: "environment" },
        config,
        (decodedText) => {
          // Code-barres scanné avec succès
          stopScanning();
          handleVerification(decodedText.trim());
        },
        (errorMessage) => {
          // Erreur ignorée pendant le scan (normal)
        }
      );
    } catch (err) {
      console.error('Erreur lors du démarrage du scanner:', err);
      setError('Impossible de démarrer le scanner. Vérifiez que vous avez autorisé l\'accès à la caméra.');
      setScanning(false);
    }
  };

  useEffect(() => {
    if (scanning) {
      // Démarrer le scanner une fois que l'élément est rendu
      const timer = setTimeout(() => {
        startScanning();
      }, 200);
      
      return () => {
        clearTimeout(timer);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
        html5QrCodeRef.current.clear().catch(() => {});
      }
    };
  }, []);

  return (
    <div className="scan-page-wrapper">
      <div className="scan-container">
        <div className="scan-box">
        <div className="scan-header">
          <div className="logo-container">
            <img src="/logo.png" alt="MUTU NDE ESPOIR ASBL" className="logo" />
          </div>
          <h1>MUTU NDE ESPOIR ASBL</h1>
          <h2>Vérification du statut de cotisation</h2>
          <p className="scan-subtitle">Saisissez le numéro matricule ou scannez le code-barres</p>
        </div>

        {!result && !scanning && (
          <div className="scan-actions">
            {/* Sélecteur de type de recherche */}
            <div className="search-type-selector" style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '10px',
              marginBottom: '20px'
            }}>
              <button
                type="button"
                onClick={() => setSearchType('membre')}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: searchType === 'membre' ? '2px solid #2e7d32' : '2px solid #ddd',
                  backgroundColor: searchType === 'membre' ? '#e8f5e9' : '#fff',
                  color: searchType === 'membre' ? '#2e7d32' : '#666',
                  fontWeight: searchType === 'membre' ? '600' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                👤 Membre
              </button>
              <button
                type="button"
                onClick={() => setSearchType('famille')}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: searchType === 'famille' ? '2px solid #1565c0' : '2px solid #ddd',
                  backgroundColor: searchType === 'famille' ? '#e3f2fd' : '#fff',
                  color: searchType === 'famille' ? '#1565c0' : '#666',
                  fontWeight: searchType === 'famille' ? '600' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                👨‍👩‍👧‍👦 Famille
              </button>
            </div>

            <div className="scan-methods">
              <button 
                type="button"
                className="btn btn-primary btn-scan" 
                onClick={() => setScanning(true)}
                disabled={loading || scanning}
              >
                <span className="btn-icon">📷</span>
                <span>Scanner le code-barres</span>
              </button>
              <div className="scan-divider">
                <span>OU</span>
              </div>
            </div>
            <form onSubmit={handleManualInput} className="manual-input-form">
              <div className="form-group">
                <label>
                  <span className="label-icon">🔢</span>
                  {searchType === 'famille' ? 'Numéro de la famille' : 'Numéro matricule du membre'}
                </label>
                <input
                  type="text"
                  name="numero"
                  placeholder={searchType === 'famille' ? 'Ex: FAM531976' : 'Ex: MNE-001'}
                  required
                  className="code-input"
                  autoFocus
                />
                <small className="input-hint">Entrez le numéro matricule du membre (ex: MNE-001)</small>
              </div>
              <button type="submit" className="btn btn-primary btn-verify" disabled={loading}>
                <span className="btn-icon">🔍</span>
                <span>{loading ? 'Vérification en cours...' : 'Vérifier le statut'}</span>
              </button>
            </form>
          </div>
        )}

        {scanning && (
          <div className="scanner-section">
            <div className="scanner-container">
              <div id="reader" style={{ width: '100%', maxWidth: '500px', margin: '0 auto' }}></div>
              <button 
                className="btn btn-secondary btn-stop-scan" 
                onClick={stopScanning}
              >
                <span className="btn-icon">⏹️</span>
                <span>Arrêter le scan</span>
              </button>
              <p className="scan-instruction">Pointez la caméra vers le code-barres de la carte</p>
            </div>
          </div>
        )}

        {loading && (
          <div className="loading-container">
            <div className="spinner-large"></div>
            <p className="loading-text">Vérification en cours...</p>
            <p className="loading-subtext">Veuillez patienter</p>
          </div>
        )}

        {error && (
          <div className="alert-container alert-error">
            <div className="alert-icon">⚠️</div>
            <div className="alert-content">
              <h4>Erreur de vérification</h4>
              <p>{error}</p>
            </div>
            <button className="btn btn-secondary btn-retry" onClick={reset}>
              Réessayer
            </button>
          </div>
        )}

        {result && (
          <div className="result-container">
            {/* En-tête avec statut visuel */}
            <div className={`result-header ${result.enOrdre ? 'result-header-ok' : 'result-header-ko'}`}>
              <div className="result-status-badge">
                <div className="result-icon-large">
                  {result.enOrdre ? '✓' : '✗'}
                </div>
                <div className="result-status-text">
                  <h3>{result.enOrdre ? 'Statut : En ordre' : 'Statut : Non en ordre'}</h3>
                  <p className="result-status-subtitle">
                    {result.enOrdre 
                      ? result.type === 'famille' 
                        ? 'La famille est à jour avec ses cotisations'
                        : 'Le membre est à jour avec ses cotisations' 
                      : result.type === 'famille'
                        ? result.evolutionCotisations?.length === 0
                          ? 'Aucune cotisation enregistrée pour cette famille'
                          : `${result.cotisationsImpayees} cotisation(s) impayée(s)`
                        : result.totalCotisations === 0
                          ? 'Aucune cotisation enregistrée pour ce membre'
                          : `${result.cotisationsImpayees} cotisation(s) impayée(s)`}
                  </p>
                </div>
              </div>
            </div>

            {/* Affichage FAMILLE */}
            {result.type === 'famille' ? (
              <div className="result-main-content">
                {/* Photo du chef de famille */}
                {result.famille?.photo_chef && (
                  <div className="member-photo-section">
                    <div className="photo-frame">
                      <img 
                        src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${result.famille.photo_chef}`}
                        alt={result.famille?.chef_famille_nom}
                        className="member-photo"
                      />
                    </div>
                  </div>
                )}
                
                {/* Informations de la famille */}
                <div className="result-info-section">
                  <div className="result-info-card">
                    <div className="card-section-header">
                      <span className="section-icon">👨‍👩‍👧‍👦</span>
                      <h4>Informations de la famille</h4>
                    </div>
                    <div className="info-table-container">
                      <table className="info-table">
                        <tbody>
                          <tr>
                            <td className="info-table-label">Numéro famille</td>
                            <td className="info-table-value info-value-highlight">{result.famille?.numero_famille}</td>
                          </tr>
                          <tr>
                            <td className="info-table-label">Nom de la famille</td>
                            <td className="info-table-value">{result.famille?.nom_famille}</td>
                          </tr>
                          <tr>
                            <td className="info-table-label">Chef de famille</td>
                            <td className="info-table-value">{result.famille?.chef_famille_nom}</td>
                          </tr>
                          {result.famille?.telephone && (
                            <tr>
                              <td className="info-table-label">Téléphone</td>
                              <td className="info-table-value">{result.famille?.telephone}</td>
                            </tr>
                          )}
                          {result.famille?.adresse && (
                            <tr>
                              <td className="info-table-label">Adresse</td>
                              <td className="info-table-value">{result.famille?.adresse}</td>
                            </tr>
                          )}
                          {result.famille?.quartier && (
                            <tr>
                              <td className="info-table-label">Quartier</td>
                              <td className="info-table-value">{result.famille?.quartier}</td>
                            </tr>
                          )}
                          {result.famille?.commune && (
                            <tr>
                              <td className="info-table-label">Commune</td>
                              <td className="info-table-value">{result.famille?.commune}</td>
                            </tr>
                          )}
                          <tr>
                            <td className="info-table-label">Nombre de membres</td>
                            <td className="info-table-value">{result.nombreMembresEnregistres || result.famille?.nombre_membres || 0}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Membres de la famille */}
                  {result.membres && result.membres.length > 0 && (
                    <div className="result-info-card">
                      <div className="card-section-header">
                        <span className="section-icon">👥</span>
                        <h4>Membres de la famille</h4>
                        <span className="history-count">({result.membres.length} membre{result.membres.length > 1 ? 's' : ''})</span>
                      </div>
                      <div className="table-container">
                        <table className="cotisation-table">
                          <thead>
                            <tr>
                              <th>Photo</th>
                              <th>Nom</th>
                              <th>Prénom</th>
                              <th>Lien</th>
                              <th>Date de naissance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.membres.map((membre, index) => (
                              <tr key={membre.id || index}>
                                <td style={{ textAlign: 'center' }}>
                                  {membre.photo ? (
                                    <img 
                                      src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${membre.photo}`}
                                      alt={membre.prenom}
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        border: '2px solid #e0e0e0'
                                      }}
                                    />
                                  ) : (
                                    <div style={{
                                      width: '40px',
                                      height: '40px',
                                      borderRadius: '50%',
                                      backgroundColor: '#f0f0f0',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      margin: '0 auto',
                                      color: '#999',
                                      fontSize: '18px'
                                    }}>
                                      👤
                                    </div>
                                  )}
                                </td>
                                <td>{membre.nom}</td>
                                <td>{membre.prenom}</td>
                                <td>
                                  <span style={{
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    backgroundColor: membre.lien_parente === 'Chef' ? '#e8f5e9' : 
                                                    membre.lien_parente === 'Épouse' ? '#fce4ec' : 
                                                    membre.lien_parente === 'Enfant' ? '#e3f2fd' : '#f5f5f5',
                                    color: membre.lien_parente === 'Chef' ? '#2e7d32' : 
                                           membre.lien_parente === 'Épouse' ? '#c2185b' : 
                                           membre.lien_parente === 'Enfant' ? '#1565c0' : '#666'
                                  }}>
                                    {membre.lien_parente || 'Membre'}
                                  </span>
                                </td>
                                <td>
                                  {membre.date_naissance 
                                    ? new Date(membre.date_naissance).toLocaleDateString('fr-FR', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                      })
                                    : '-'
                                  }
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Évolution des cotisations famille */}
                  {result.evolutionCotisations && result.evolutionCotisations.length > 0 && (
                    <div className="result-info-card">
                      <div className="card-section-header">
                        <span className="section-icon">📊</span>
                        <h4>Évolution des cotisations</h4>
                      </div>
                      <div className="table-container">
                        <table className="cotisation-table">
                          <thead>
                            <tr>
                              <th>Mois</th>
                              <th>Année</th>
                              <th>Statut</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.evolutionCotisations.map((cotisation, index) => {
                              const moisNoms = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
                              const isPaid = cotisation.statut === 'payee';
                              return (
                                <tr key={cotisation.id || index} style={{ backgroundColor: isPaid ? '#f1f8e9' : '#fff5f5' }}>
                                  <td>{moisNoms[cotisation.mois]}</td>
                                  <td>{cotisation.annee}</td>
                                  <td>
                                    <span style={{
                                      padding: '4px 12px',
                                      borderRadius: '20px',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      backgroundColor: isPaid ? '#4caf50' : '#f44336',
                                      color: 'white'
                                    }}>
                                      {isPaid ? '✓ Payé' : '✗ Impayé'}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                </div>
              </div>
            ) : (
              /* Affichage MEMBRE (existant) */
              <div className="result-main-content">
                {/* Photo du membre */}
                {result.membre?.photo && (
                  <div className="member-photo-section">
                    <div className="photo-frame">
                      <img 
                        src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${result.membre.photo}`}
                        alt={`${result.membre.nom} ${result.membre.prenom}`}
                        className="member-photo"
                      />
                    </div>
                  </div>
                )}
                
                {/* Informations principales */}
                <div className="result-info-section">
                  <div className="result-info-card">
                    <div className="card-section-header">
                      <span className="section-icon">👤</span>
                      <h4>Informations personnelles</h4>
                    </div>
                    <div className="info-table-container">
                      <table className="info-table">
                        <tbody>
                          <tr>
                            <td className="info-table-label">Numéro matricule</td>
                            <td className="info-table-value info-value-highlight">{result.membre?.numero_matricule}</td>
                          </tr>
                          <tr>
                            <td className="info-table-label">Nom complet</td>
                            <td className="info-table-value">{result.membre?.nom} {result.membre?.postnom || ''} {result.membre?.prenom}</td>
                          </tr>
                          <tr>
                            <td className="info-table-label">Date de naissance</td>
                            <td className="info-table-value">
                              {result.membre?.date_naissance 
                                ? new Date(result.membre.date_naissance).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                : '-'
                              }
                            </td>
                          </tr>
                          <tr>
                            <td className="info-table-label">Lieu de naissance</td>
                            <td className="info-table-value">{result.membre?.lieu_naissance || '-'}</td>
                          </tr>
                          {result.membre?.telephone && (
                            <tr>
                            <td className="info-table-label">Téléphone</td>
                            <td className="info-table-value">{result.membre.telephone}</td>
                          </tr>
                        )}
                        {result.membre.profession && (
                          <tr>
                            <td className="info-table-label">Profession</td>
                            <td className="info-table-value">{result.membre.profession}</td>
                          </tr>
                        )}
                        {(result.membre.quartier || result.membre.commune) && (
                          <tr>
                            <td className="info-table-label">Adresse</td>
                            <td className="info-table-value">
                              {result.membre.quartier && result.membre.commune 
                                ? `${result.membre.quartier}, ${result.membre.commune}`
                                : result.membre.quartier || result.membre.commune
                              }
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Liste des enfants du membre */}
                {result.enfants && result.enfants.length > 0 && (
                  <div className="result-info-card">
                    <div className="card-section-header">
                      <span className="section-icon">👨‍👩‍👧‍👦</span>
                      <h4>Enfants du membre</h4>
                      <span className="history-count">({result.enfants.length} enfant{result.enfants.length > 1 ? 's' : ''})</span>
                    </div>
                    <div className="table-container">
                      <table className="cotisation-table">
                        <thead>
                          <tr>
                            <th>Photo</th>
                            <th>Nom</th>
                            <th>Prénom</th>
                            <th>Lien</th>
                            <th>Date de naissance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.enfants.map((enfant, index) => (
                              <tr key={enfant.id || index}>
                                <td style={{ textAlign: 'center' }}>
                                  {enfant.photo ? (
                                    <img 
                                      src={enfant.photo} 
                                      alt={`${enfant.prenom}`}
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        border: '2px solid #e0e0e0'
                                      }}
                                    />
                                  ) : (
                                    <div style={{
                                      width: '40px',
                                      height: '40px',
                                      borderRadius: '50%',
                                      backgroundColor: '#f0f0f0',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      margin: '0 auto',
                                      color: '#999',
                                      fontSize: '18px'
                                    }}>
                                      👤
                                    </div>
                                  )}
                                </td>
                                <td>{enfant.nom}</td>
                                <td>{enfant.prenom}</td>
                                <td>
                                  <span style={{
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    backgroundColor: enfant.lien === 'Épouse' ? '#e8f5e9' : 
                                                    enfant.lien === 'Fils' ? '#e3f2fd' : 
                                                    enfant.lien === 'Fille' ? '#fce4ec' : '#f5f5f5',
                                    color: enfant.lien === 'Épouse' ? '#2e7d32' : 
                                           enfant.lien === 'Fils' ? '#1565c0' : 
                                           enfant.lien === 'Fille' ? '#c2185b' : '#666'
                                  }}>
                                    {enfant.lien || 'Enfant'}
                                  </span>
                                </td>
                                <td>
                                  {enfant.date_naissance 
                                    ? new Date(enfant.date_naissance).toLocaleDateString('fr-FR', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                      })
                                    : '-'
                                  }
                                </td>
                              </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Évolution des cotisations - Regroupées par mois */}
                {result.evolutionCotisations && result.evolutionCotisations.length > 0 && (() => {
                  // Regrouper les cotisations par mois
                  const moisNoms = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
                  const cotisationsParMois = {};
                  
                  result.evolutionCotisations.forEach(cot => {
                    const key = `${cot.annee}-${cot.mois}`;
                    if (!cotisationsParMois[key]) {
                      cotisationsParMois[key] = {
                        annee: cot.annee,
                        mois: cot.mois,
                        periode: `${moisNoms[cot.mois]} ${cot.annee}`,
                        montantTotal: 0,
                        cotisations: [],
                        toutesPayees: true,
                        auMoinsUnePayee: false,
                        dernierPaiement: null
                      };
                    }
                    cotisationsParMois[key].montantTotal += cot.montant;
                    cotisationsParMois[key].cotisations.push(cot);
                    if (cot.statut !== 'paye') {
                      cotisationsParMois[key].toutesPayees = false;
                    } else {
                      cotisationsParMois[key].auMoinsUnePayee = true;
                      if (!cotisationsParMois[key].dernierPaiement || 
                          new Date(cot.date_paiement) > new Date(cotisationsParMois[key].dernierPaiement)) {
                        cotisationsParMois[key].dernierPaiement = cot.date_paiement;
                      }
                    }
                  });
                  
                  // Convertir en tableau et trier
                  const moisRegroupes = Object.values(cotisationsParMois).sort((a, b) => {
                    if (a.annee !== b.annee) return b.annee - a.annee;
                    return b.mois - a.mois;
                  });

                  return (
                    <div className="cotisation-evolution-card">
                      <div className="card-section-header">
                        <span className="section-icon">📊</span>
                        <h4>Évolution des cotisations</h4>
                        <span className="history-count">({moisRegroupes.length} mois)</span>
                      </div>
                      <div className="table-container">
                        <table className="cotisation-table">
                          <thead>
                            <tr>
                              <th>Période</th>
                              <th>Semaines</th>
                              <th>Statut</th>
                              <th>Date paiement</th>
                            </tr>
                          </thead>
                          <tbody>
                            {moisRegroupes.map((moisData, index) => {
                              // Déterminer le statut global du mois
                              let statut = 'impaye';
                              let statutTexte = '✗ Impayé';
                              let statutClass = 'status-impaye';
                              
                              if (moisData.toutesPayees) {
                                statut = 'paye';
                                statutTexte = '✓ Payé';
                                statutClass = 'status-paye';
                              } else if (moisData.auMoinsUnePayee) {
                                statut = 'partiel';
                                statutTexte = '◐ Partiel';
                                statutClass = 'status-partiel';
                              }
                              
                              // Afficher les semaines avec indicateur de paiement
                              const semainesAvecStatut = moisData.cotisations
                                .filter(c => c.semaine)
                                .sort((a, b) => a.semaine - b.semaine)
                                .map(c => ({
                                  semaine: c.semaine,
                                  paye: c.statut === 'paye'
                                }));
                              
                              return (
                                <tr key={`${moisData.annee}-${moisData.mois}`} className={statut === 'paye' ? 'row-paye' : 'row-impaye'}>
                                  <td className="table-period">{moisData.periode}</td>
                                  <td style={{ fontSize: '12px' }}>
                                    {semainesAvecStatut.length > 0 ? (
                                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                        {semainesAvecStatut.map(s => (
                                          <span 
                                            key={s.semaine}
                                            style={{
                                              padding: '2px 8px',
                                              borderRadius: '4px',
                                              fontSize: '11px',
                                              fontWeight: '600',
                                              backgroundColor: s.paye ? '#d4edda' : '#f8d7da',
                                              color: s.paye ? '#155724' : '#721c24',
                                              border: `1px solid ${s.paye ? '#28a745' : '#dc3545'}`
                                            }}
                                          >
                                            {s.paye ? '✓' : '✗'} S{s.semaine}
                                          </span>
                                        ))}
                                      </div>
                                    ) : '-'}
                                  </td>
                                  <td className="table-status">
                                    <span className={`status-badge ${statutClass}`} style={
                                      statut === 'partiel' ? { backgroundColor: '#fff3cd', color: '#856404' } : {}
                                    }>
                                      {statutTexte}
                                    </span>
                                  </td>
                                  <td className="table-date">
                                    {moisData.dernierPaiement 
                                      ? new Date(moisData.dernierPaiement).toLocaleDateString('fr-FR', {
                                          day: '2-digit',
                                          month: '2-digit',
                                          year: 'numeric'
                                        })
                                      : '-'
                                    }
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

              </div>
            </div>
            )}

            {/* Actions */}
            <div className="result-actions">
              <button className="btn btn-primary btn-new-scan" onClick={reset}>
                <span className="btn-icon">🔄</span>
                <span>Nouvelle vérification</span>
              </button>
            </div>
          </div>
        )}

        </div>
      </div>
      <InstallPrompt />
      <Footer />
    </div>
  );
};

export default ScanCodeBarres;

